import { Box, Button, Collapse, Stack, Text, Wrap, WrapItem } from '@chakra-ui/react'
import { faEye, faEyeSlash, faSmilePlus } from '@fortawesome/pro-regular-svg-icons'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import assertDefined from '@/utils/assertDefined'

import { useUser } from '@/client/context/user'
import useAPIWrapper from '@/client/hooks/useAPIWrapper'
import RPC from '@/client/rpc'
import isEmpty from '@/client/services/richText/isEmpty'
import type { SerializedRichText } from '@/client/services/richText/types'
import { isUserInChannel } from '@/client/services/worlds'

import type {
  PostDetails,
  PostEventDetails,
  ReactionDetails,
} from '@/components/Community/Post/types'
import Alert from '@/components/UI/Alert'
import Icon from '@/components/UI/Icon/Icon'
import RichText from '@/components/UI/RichTextEditor/RichText'
import RichTextEditorSimple from '@/components/UI/RichTextEditor/RichTextEditorSimple/RichTextEditorSimple'
import type { RichTextEditorSimpleRef } from '@/components/UI/RichTextEditor/RichTextEditorSimple/types'
import FileUploadGallery from '@/components/Worlds/FileUpload/FileUploadGallery'
import { getProcessedReactions, getReactionListWithNewReaction } from '@/components/Worlds/helpers'
import { MAX_REACTIONS_TO_SHOW } from '@/components/Worlds/Reaction/constants'
import ReactionButton from '@/components/Worlds/Reaction/ReactionButton'
import ReactionWrapItems from '@/components/Worlds/Reaction/ReactionWrapItems'
import IconButtonWithNumber from '@/components/Worlds/UI/Button/IconButtonWithNumber'

import { assertPostEventType } from './helpers'
import PostEventCommentHeader from './PostEventCommentHeader'

export default function PostEventComment({
  post,
  postEvent,
  onDelete,
  isOptimistic = false,
}: {
  post: PostDetails
  postEvent: PostEventDetails
  onDelete: (p: PostEventDetails) => void
  isOptimistic?: boolean
}): JSX.Element | null {
  const user = useUser()
  const router = useRouter()

  const editorRef = useRef<RichTextEditorSimpleRef>(null)

  assertPostEventType(postEvent, 'comment')
  const { body, attachments, reactions, isCollapsible } = postEvent

  assertDefined(body, 'PostEventComment has no body')

  const canUserReact = isUserInChannel({ user, channelId: post.channel.id })

  const [isEditing, setIsEditing] = useState(false)
  const [editedBody, setEditedBody] = useState<SerializedRichText>(body)
  const [optimisticBody, setOptimisticBody] = useState<SerializedRichText>(body)
  const [blockedError, setBlockedError] = useState<string | null>(null)
  const [showAllReactions, setShowAllReactions] = useState(false)
  const [showCollapsibleComment, setShowCollapsibleComment] = useState(false)

  const parsedReactions = getProcessedReactions({ reactions })

  const [currentReactions, setCurrentReactions] =
    useState<{ reaction: string; reactionUsers: ReactionDetails[] }[]>(parsedReactions)

  useEffect(() => setCurrentReactions(getProcessedReactions({ reactions })), [reactions])

  const [onToggleReaction] = useAPIWrapper(
    async (newReaction: string) => {
      // Optimistic update
      const oldReactions = [...currentReactions]

      const newReactions = getReactionListWithNewReaction({
        reactions: currentReactions,
        newReaction,
        userId: user.id,
        userDisplayName: user.displayName,
      })

      setCurrentReactions(newReactions)

      if (
        oldReactions.length <= MAX_REACTIONS_TO_SHOW &&
        newReactions.length > MAX_REACTIONS_TO_SHOW
      ) {
        // If we pass the threshold, expand all reactions to reduce potential confusion
        setShowAllReactions(true)
      }

      try {
        await RPC.togglePostEventReaction(
          {
            postEventId: postEvent.id,
            reaction: newReaction,
          },
          {
            disableProgressBar: true,
          }
        )
      } catch (err) {
        // Revert optimistic update
        setCurrentReactions(oldReactions)
        throw err
      }
    },
    [postEvent.id, user.id, currentReactions, user.displayName],
    'Toggle Post EventReaction'
  )

  const isEditedBodyEmpty = useMemo(() => isEmpty(editedBody), [editedBody])

  useEffect(() => {
    setEditedBody(body)
    setOptimisticBody(body)
  }, [body])

  const [onEdit] = useAPIWrapper(
    async () => {
      setIsEditing(false)
      setOptimisticBody(editedBody)
      setBlockedError(null)

      const editPostCommentResult = await RPC.editPostComment({
        postEventId: postEvent.id,
        body: editedBody,
      })

      if (editPostCommentResult.status === 'blocked') {
        setBlockedError(editPostCommentResult.message)
        setOptimisticBody(body)
        setIsEditing(true)
        return
      }

      if (editPostCommentResult.status === 'suspended') {
        router.reload()
      }
    },
    [editedBody, postEvent.id, body, router],
    'Delete Post Comment'
  )

  const onCancelEdit = useCallback(() => {
    setIsEditing(false)
    setEditedBody(body)
    setBlockedError(null)
  }, [body])

  if (body == null) {
    // This shouldn't happen in practice
    return null
  }

  return (
    <Stack
      direction="column"
      spacing="0px"
      _hover={{ bg: 'bg.subtle' }}
      role="group"
      px="24px"
      py="8px"
    >
      <PostEventCommentHeader
        postEvent={postEvent}
        onEdit={() => setIsEditing(true)}
        onDelete={onDelete}
        isOptimistic={isOptimistic}
        onToggleReaction={onToggleReaction}
      />
      <Stack pl="44px" direction="column" spacing="16px">
        {blockedError == null ? null : (
          <Alert variant="error">
            <Text>{blockedError}</Text>
          </Alert>
        )}
        {isEditing ? (
          <RichTextEditorSimple
            ref={editorRef}
            name="EditPostComment"
            initialValue={editedBody}
            onEnter={onEdit}
            onChange={setEditedBody}
            disableNewLineOnEnter
            rightFooterContent={() => (
              <Stack direction="row" spacing="4px">
                <Button variant="outline" minWidth="60px" size="sm" onClick={onCancelEdit}>
                  Cancel
                </Button>
                <Button onClick={onEdit} isDisabled={isEditedBodyEmpty} minWidth="60px" size="sm">
                  Save
                </Button>
              </Stack>
            )}
          />
        ) : (
          <Stack direction="column" spacing="16px">
            {isCollapsible ? (
              <Box>
                <Button
                  onClick={() => setShowCollapsibleComment(x => !x)}
                  variant="ghost"
                  leftIcon={<Icon faIcon={showCollapsibleComment ? faEyeSlash : faEye} size="md" />}
                >
                  {showCollapsibleComment ? 'Hide Comment' : 'View Comment'}
                </Button>
                <Collapse in={showCollapsibleComment}>
                  <RichText content={optimisticBody} />
                </Collapse>
              </Box>
            ) : (
              <RichText content={optimisticBody} />
            )}
            <FileUploadGallery uploads={attachments} />
            {currentReactions.some(({ reactionUsers }) => reactionUsers.length > 0) ? (
              <Wrap spacing="8px">
                <ReactionWrapItems
                  reactions={currentReactions}
                  canUserReact={canUserReact}
                  onToggleReaction={onToggleReaction}
                  showAll={showAllReactions}
                  setShowAll={setShowAllReactions}
                />
                <WrapItem>
                  <ReactionButton
                    onReact={onToggleReaction}
                    buttonComponent={({ active }) => (
                      <IconButtonWithNumber icon={faSmilePlus} highlighted={active} />
                    )}
                  />
                </WrapItem>
              </Wrap>
            ) : null}
          </Stack>
        )}
      </Stack>
    </Stack>
  )
}
